/**
 * FRONT-1642 (review-fixed): highlight a search term inside already-rendered,
 * sanitized Markdown HTML (see `~/components/markdown-renderer.tsx`) — used
 * by the Files detail page to show WHERE the term(s) the user searched for
 * in the command palette land in a document's extracted text.
 *
 * This is the risky part of the feature: it runs on model-authored HTML,
 * and `marked` passes literal, raw HTML the author wrote directly straight
 * through UNTOUCHED — neither the tags NOR the text between them are
 * entity-escaped in that case (entity-escaping only happens to text `marked`
 * itself renders FROM Markdown syntax). So this must never touch markup —
 * only the literal text between tags — and it must not assume every text
 * segment it sees was entity-escaped by `marked`.
 *
 * Four design choices:
 *
 *  1. The string is split into alternating TAG-OR-COMMENT and TEXT segments.
 *     The tag matcher is ATTRIBUTE-AWARE: a quoted attribute value
 *     (`'...'`/`"..."`) is one opaque unit even if it contains a `>` (a
 *     URL's query string routinely does), and an HTML comment (`<!-- … -->`)
 *     is matched as a whole even if its body contains a `>`. A `<` is only
 *     treated as the start of a tag/comment when the very next character is
 *     a letter, `/`, `!`, or `?` — exactly how a real HTML parser reads a
 *     bare `<` in text (e.g. "5 < 10 is true") as a literal character, not
 *     markup. An unterminated tag/comment at the end of the string is
 *     swallowed whole as a single opaque segment rather than left open, so
 *     truncated markup can't leak a `<mark>` into what was meant to be a
 *     tag. Every tag/comment segment is passed through completely
 *     untouched; only TEXT segments are ever rewritten.
 *  2. Matching uses a case-insensitive `RegExp` built from the query (with
 *     regex metacharacters escaped — the query is always a literal string,
 *     never a pattern) — never `.toLowerCase()` + `indexOf`. Lower-casing a
 *     string can change its LENGTH for some characters (`'İ'.toLowerCase()`
 *     is two code points), which silently drifts every match index found
 *     downstream of the first one; a regex's `i` flag case-folds during
 *     comparison without ever altering the string or its indices.
 *  3. The query is split on whitespace into independent TERMS and each is
 *     highlighted on its own, rather than requiring the whole phrase to
 *     appear verbatim. The search hit this feeds from comes out of
 *     stemmed, per-token Postgres full-text search, so a query like "annual
 *     reports" routinely won't appear as one contiguous phrase even when
 *     it's clearly the right document (the source text says "annual
 *     report") — requiring an exact phrase match made the highlighter look
 *     broken on exactly the searches that actually found something.
 *     Matches from every term are merged when they overlap or sit directly
 *     adjacent (no gap between them), so two `<mark>` tags can never nest or
 *     abut with nothing rendered between them. A STEMMED variant ("reports"
 *     vs. a source that only ever says "report") is still not matched —
 *     that's a known, accepted limit: real stemmed matching would need the
 *     same dictionary Postgres FTS uses, which this pure client-side helper
 *     doesn't have.
 *  4. The query is escaped to mirror `marked`'s NO-ENCODE text-escaping
 *     (see `escapeTermLikeMarked` below) — the variant it actually applies
 *     to ordinary text tokens — not the stricter ENCODE variant (used only
 *     for code blocks), which used to be mirrored here by mistake and
 *     escaped every `&` unconditionally even when the source text already
 *     left an entity-shaped `&whatever;` alone.
 *
 * `html` must already be sanitized (see `markdown-renderer.tsx`'s
 * `sanitize`) — this function does not remove active content, it only adds
 * `<mark>` wrappers around matched text between existing tags.
 *
 * An empty or whitespace-only query is a no-op, returning `html` unchanged.
 */

/**
 * Mirrors `marked`'s NO-ENCODE escape table exactly — the variant its
 * `escape2(text)` helper applies to ordinary TEXT tokens (called with no
 * second argument; see `other.escapeReplaceNoEncode` in
 * `node_modules/marked/lib/marked.cjs`), as opposed to the stricter ENCODE
 * variant (`escape2(text, true)`) it reserves for code-block content. `<`,
 * `>`, `"`, `'` are always escaped; `&` is escaped ONLY when it is not
 * already the start of what looks like a character reference (`&#123;`,
 * `&#x1F;`, `&amp;`) — `marked` assumes an ampersand shaped like that is
 * already an entity and leaves it alone, so a document that legitimately
 * contains `&whatever;` untouched can still be found by a query containing
 * a bare `&`.
 */
const NO_ENCODE_PATTERN = /[<>"']|&(?!(#\d{1,7}|#[Xx][a-fA-F0-9]{1,6}|\w+);)/g;
const ESCAPE_MAP: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
};

/**
 * Escapes `term` the way `marked`'s no-encode escaper would if `term` were
 * the ENTIRE piece of text being escaped. This is necessarily an
 * approximation of what `marked` does to the full document: its "is this
 * `&` already an entity" lookahead can see characters that live in the
 * source text just past the end of a search term — e.g. document text
 * "AT&T;" keeps its `&` unescaped because "T;" completes the `\w+;` shape,
 * but a query of "AT&T" ends right before the `;`, so the identical
 * lookahead evaluated against the query ALONE doesn't see a completed
 * entity and escapes the `&` anyway, producing "AT&amp;T" — which then
 * fails to find the source's literal "AT&T;". Getting that fully right
 * would mean escaping the term in the context of the surrounding document
 * text rather than in isolation, which isn't practical for a term the user
 * is actively typing against source text of unknown shape. This is still a
 * strict improvement over unconditionally escaping every `&` (the old
 * encode-table mirror) for every query that doesn't straddle exactly that
 * boundary — see `highlight-html.test.ts`'s corpus, including a test that
 * pins the still-unmatched "AT&T;"/"AT&T" case as a documented limit rather
 * than a silent gap.
 */
function escapeTermLikeMarked(term: string): string {
  return term.replace(NO_ENCODE_PATTERN, (ch) => ESCAPE_MAP[ch] ?? ch);
}

/** Escapes every character with special meaning in a JS `RegExp` pattern,
 *  so a query containing `.`, `*`, `(`, `[a-z]`, etc. is matched as a
 *  literal string, never interpreted as a pattern. */
function escapeRegExpMetachars(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** A `<` is only the start of a tag/comment when immediately followed by a
 *  real tag-start character — a letter, `/` (closing tag), `!` (comment or
 *  doctype), or `?` (processing instruction). Anything else (a space, a
 *  digit, end of string) is how a real HTML parser reads a bare `<` as a
 *  literal character in text, e.g. "5 < 10 is true" — matching that keeps a
 *  stray `<` in prose from being mistaken for the start of markup. */
const TAG_START = '[a-zA-Z!/?]';

/** A quoted attribute value is one opaque unit even when it contains a `>`
 *  — a URL's query string commonly does (`href='https://x.test/a?q=1>2'`).
 */
const QUOTED = `"[^"]*"|'[^']*'`;

/**
 * Tokenizes `html` into alternating TAG-OR-COMMENT and TEXT segments,
 * attribute- and comment-aware (see the module header). Alternatives are
 * tried in order at each position: a comment (closed, or — at end of input
 * — unterminated) first, since its body's own `>` must never be mistaken
 * for the comment's end by the plainer tag pattern below it; then an
 * ordinary tag (closed, or unterminated at end of input); then everything
 * else as TEXT.
 */
const TOKEN_PATTERN = new RegExp(
  [
    // HTML comment, closed or (at end of input) unterminated.
    '<!--[\\s\\S]*?-->',
    '<!--[\\s\\S]*$',
    // An ordinary tag: <, then any mix of quoted attribute values (opaque,
    // may contain >) and non-quote/non-> characters, then a closing >.
    `<(?=${TAG_START})(?:${QUOTED}|[^"'>])*>`,
    // The same, but unterminated at end of input — swallowed whole rather
    // than left open, so truncated markup can't leak a <mark> into what
    // was meant to be a tag/attribute.
    `<(?=${TAG_START})(?:${QUOTED}|[^"'>])*$`,
    // Everything else is TEXT: any non-< character, or a < that isn't the
    // start of a tag/comment per TAG_START above.
    `(?:[^<]|<(?!${TAG_START}))+`,
  ].join('|'),
  'g',
);

/** A matched segment is a tag/comment iff it starts with `<` immediately
 *  followed by a real tag-start character — the exact condition the tag
 *  alternatives above require to fire, so this can never disagree with the
 *  tokenizer about which segment kind it's looking at. A TEXT segment can
 *  also start with a literal `<` (the stray, non-tag-starting case), so
 *  "starts with <" alone isn't enough to tell them apart. */
function isTagSegment(segment: string): boolean {
  return segment.length > 0 && segment[0] === '<' && /^[a-zA-Z!/?]/.test(segment.slice(1));
}

interface MatchRange {
  start: number;
  end: number;
}

/**
 * Every match of every term's search pattern within one TEXT segment,
 * merged so overlapping or directly-touching ranges — across different
 * terms, or repeats of the same term — become a single span. Two `<mark>`
 * tags must never nest or sit back-to-back with nothing rendered between
 * them.
 */
function findMergedMatchRanges(text: string, termPatterns: RegExp[]): MatchRange[] {
  const raw: MatchRange[] = [];
  for (const pattern of termPatterns) {
    pattern.lastIndex = 0;
    let match: RegExpExecArray | null;
    while ((match = pattern.exec(text))) {
      const start = match.index;
      const end = start + match[0].length;
      raw.push({ start, end });
      // Every term is non-empty (queries are trimmed and whitespace-split
      // before a pattern is ever built), so a zero-length match can't
      // happen today — guarded anyway so a future change here can't turn
      // into an infinite loop.
      if (pattern.lastIndex === match.index) pattern.lastIndex += 1;
    }
  }
  if (raw.length === 0) return [];
  raw.sort((a, b) => a.start - b.start || a.end - b.end);
  const merged: MatchRange[] = [{ ...raw[0] }];
  for (let i = 1; i < raw.length; i++) {
    const current = raw[i];
    const last = merged[merged.length - 1];
    if (current.start <= last.end) {
      last.end = Math.max(last.end, current.end);
    } else {
      merged.push({ ...current });
    }
  }
  return merged;
}

/** Wraps every merged range in `<mark>`, keeping everything outside a range
 *  byte-identical. */
function wrapRanges(text: string, ranges: MatchRange[]): string {
  if (ranges.length === 0) return text;
  let out = '';
  let cursor = 0;
  for (const { start, end } of ranges) {
    out += text.slice(cursor, start);
    out += `<mark>${text.slice(start, end)}</mark>`;
    cursor = end;
  }
  out += text.slice(cursor);
  return out;
}

/**
 * Highlight every occurrence of every whitespace-separated term in `query`
 * inside `html`'s text nodes with `<mark>` — see the module header for the
 * full design. `html` must already be sanitized.
 */
export function highlightHtml(html: string, query: string): string {
  const terms = query
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (terms.length === 0) return html;
  const termPatterns = terms.map(
    (term) => new RegExp(escapeRegExpMetachars(escapeTermLikeMarked(term)), 'gi'),
  );
  return html.replace(TOKEN_PATTERN, (segment) => {
    if (isTagSegment(segment)) return segment;
    const ranges = findMergedMatchRanges(segment, termPatterns);
    return wrapRanges(segment, ranges);
  });
}
